import type { JSX } from 'react'

import { useUnit } from 'effector-react'
import * as era from '~/core/era'
import { $movieNightEnabled } from '~/core/flags/options'
import { ui } from '~/core/skin'
import { entitiesHybridSearchModel } from '~/entities/hybrid-search'
import { entitiesMotnModel } from '~/entities/motn'
import { AppTitle } from '~/shared/ui/components/AppTitle'
import * as hybridSearchModel from './model'

export function Title(): JSX.Element {
  return <AppTitle t='search' />
}

const skin = era.skin('routes/search/hybrid')

function ClassicHybridSearchContent(): JSX.Element {
  const hybridPending = useUnit(entitiesHybridSearchModel.$hybridPending)
  const hybridSearchResults = useUnit(
    entitiesHybridSearchModel.$hybridSearchResults
  )
  const hybridHasResults = hybridSearchResults.length > 0
  const renderEmptyNoVideoViewsPage = useUnit(
    hybridSearchModel.$renderEmptyNoVideoViewsPage
  )
  const renderEmptyNoResultsPage = useUnit(
    hybridSearchModel.$renderEmptyNoResultsPage
  )

  return (
    <>
      {hybridPending && <ui.RenderSkin name='searchLoader' />}
      {hybridHasResults && <ui.RenderSkin name='hybridSearchResults' />}
      {renderEmptyNoVideoViewsPage && (
        <ui.RenderSkin name='hybridSearchNoVideoViewsEmptyPage' />
      )}
      {renderEmptyNoResultsPage && <ui.RenderSkin name='noResultsEmptyPage' />}
    </>
  )
}

// FP-4236: MOTN and hybrid stay independent requests. FP-4286: MOTN-derived UI
// (results, loader, no-results banner) still waits for the internal response
// (NF-180.2 UI-1.1) — do not restore FP-3960 paint tracking.
export function getMovieNightSectionVisibility({
  hybridPending,
  hybridHasResults,
  renderEmptyNoVideoViewsPage,
  motnPending,
  motnHasResults,
  motnInited,
}: {
  hybridPending: boolean
  hybridHasResults: boolean
  renderEmptyNoVideoViewsPage: boolean
  motnPending: boolean
  motnHasResults: boolean
  motnInited: boolean
}): {
  showNoResultsEmptyPage: boolean
  showMotnNoResultsBanner: boolean
  showMotnLoader: boolean
  showMotnSearchResults: boolean
} {
  const showNoResultsEmptyPage =
    !hybridHasResults &&
    !motnHasResults &&
    !renderEmptyNoVideoViewsPage &&
    !hybridPending &&
    !motnPending

  const showMotnNoResultsBanner =
    motnInited &&
    !motnHasResults &&
    !motnPending &&
    !hybridPending &&
    !showNoResultsEmptyPage

  // FP-4252: only show the MOTN loader once the internal (hybrid) request has
  // finished, so the two loaders never overlap.
  const showMotnLoader = motnPending && !hybridPending

  // FP-4286: do not paint the external grid until the main search settles.
  const showMotnSearchResults = motnHasResults && !hybridPending

  return {
    showNoResultsEmptyPage,
    showMotnNoResultsBanner,
    showMotnLoader,
    showMotnSearchResults,
  }
}

function MovieNightHybridSearchContent(): JSX.Element {
  const hybridPending = useUnit(entitiesHybridSearchModel.$hybridPending)
  const hybridSearchResults = useUnit(
    entitiesHybridSearchModel.$hybridSearchResults
  )
  const hybridHasResults = hybridSearchResults.length > 0
  const renderEmptyNoVideoViewsPage = useUnit(
    hybridSearchModel.$renderEmptyNoVideoViewsPage
  )

  const motnPending = useUnit(entitiesMotnModel.$motnPending)
  const motnSearchResults = useUnit(entitiesMotnModel.$motnSearchResults)
  const motnHasResults =
    motnSearchResults != null && motnSearchResults.length > 0
  const motnInited = motnSearchResults != null

  const {
    showNoResultsEmptyPage,
    showMotnNoResultsBanner,
    showMotnLoader,
    showMotnSearchResults,
  } = getMovieNightSectionVisibility({
    hybridPending,
    hybridHasResults,
    renderEmptyNoVideoViewsPage,
    motnPending,
    motnHasResults,
    motnInited,
  })

  return (
    <>
      {hybridPending && <ui.RenderSkin name='searchLoader' />}
      {hybridHasResults && <ui.RenderSkin name='hybridSearchResults' />}
      {renderEmptyNoVideoViewsPage && (
        <ui.RenderSkin name='hybridSearchNoVideoViewsEmptyPage' />
      )}
      {showNoResultsEmptyPage && <ui.RenderSkin name='noResultsEmptyPage' />}
      {showMotnSearchResults && <ui.RenderSkin name='motnSearchResults' />}
      {showMotnLoader && <ui.RenderSkin name='motnLoader' />}
      {showMotnNoResultsBanner && <ui.RenderSkin name='motnNoResults' />}
    </>
  )
}

HybridSearchPage.displayName = 'HybridSearchPage'
export function HybridSearchPage(): JSX.Element {
  const movieNightEnabled = useUnit($movieNightEnabled)

  return (
    <ui.SkinContext value={skin}>
      <ui.PageWrapper className='search-page hybrid-search-page'>
        <ui.RenderSkin name='stickySearchBlock' />
        {movieNightEnabled ? (
          <MovieNightHybridSearchContent />
        ) : (
          <ClassicHybridSearchContent />
        )}
      </ui.PageWrapper>
    </ui.SkinContext>
  )
}
